import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { apiUrl } from "utils/apiUrl";
import { clientSafeMessage } from "utils/clientSafeMessage";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [status, setStatus] = useState("loading");
  const [headline, setHeadline] = useState("Verifying…");

  useEffect(() => {
    if (!token) {
      setStatus("err");
      setHeadline("Invalid verification link");
      return undefined;
    }

    let cancelled = false;
    let redirectTimer;

    (async () => {
      try {
        const res = await fetch(
          apiUrl(`/api/verify-email?token=${encodeURIComponent(token)}`),
          { credentials: "include" },
        );
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;

        if (res.ok && data.ok) {
          setStatus("ok");
          setHeadline(
            data.alreadyVerified ? "E-mail already verified" : "E-mail verified",
          );
          redirectTimer = setTimeout(() => {
            navigate("/SignIn", { replace: true });
          }, 3500);
          return;
        }

        setStatus("err");
        setHeadline(
          clientSafeMessage(data.message, "Verification failed."),
        );
      } catch {
        if (!cancelled) {
          setStatus("err");
          setHeadline("Network error. Try again in a moment.");
        }
      }
    })();

    return () => {
      cancelled = true;
      if (redirectTimer) clearTimeout(redirectTimer);
    };
  }, [token, navigate]);

  const isOk = status === "ok";
  const isErr = status === "err";

  return (
    <div className="relative z-30 mt-16 mb-16 flex min-h-[65vh] w-full flex-col items-center justify-center gap-6 px-6 text-center md:mx-0 md:px-8 lg:mb-10">
      <div className="flex max-w-2xl flex-col gap-3">
        <h2
          className={`text-4xl font-extrabold leading-tight sm:text-5xl md:text-6xl ${
            isOk
              ? "text-green-400"
              : isErr
                ? "text-red-400"
                : "text-white"
          }`}
        >
          {headline}
        </h2>
        {isOk && (
          <p className="text-base text-gray-300">
            Redirecting you to SignIn in a few seconds…
          </p>
        )}
      </div>
      {(isErr || (!token && status !== "loading")) && (
        <Link
          to="/SignIn"
          className="linear rounded-xl bg-brand-500 px-10 py-4 text-lg font-bold text-white shadow-lg transition duration-200 hover:bg-brand-600 active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300"
        >
          Back to SignIn
        </Link>
      )}
    </div>
  );
}
